import React, { useState, useEffect } from 'react';
import { Search, MapPin, Phone, Globe, Star, MoreVertical, CheckCircle, ArrowRight, Share2, Bookmark } from 'lucide-react';

export const GoogleDominationMockup: React.FC = () => {
  const [clickCount, setClickCount] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setClickCount(prev => (prev >= 120 ? 0 : prev + 1));
    }, 100);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="w-full bg-white text-slate-900 py-16 px-4 md:px-12 font-sans border-b border-slate-100">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
         
         {/* LEFT: TEXT CONTENT */}
         <div className="lg:col-span-5 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold border border-blue-200">
               <span className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
               Google Search & Maps Rank #1
            </div>
            
            <h2 className="text-3xl md:text-5xl font-heading font-black tracking-tight text-slate-900 leading-tight">
               Aumente sua Reputação & Vendas no Google
            </h2>
            
            <p className="text-slate-600 text-sm md:text-base font-normal leading-relaxed">
               Atualização estratégica periódica da sua ficha no <strong>Google Meu Negócio (GMN)</strong> e otimização SEO para colocar sua empresa no topo das pesquisas locais.
            </p>

            {/* Performance Metric Box */}
            <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl text-left space-y-3">
               <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Desempenho de Contatos</span>
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-md border border-emerald-200">
                     +214% este mês
                  </span>
               </div>
               <div className="flex items-baseline gap-3">
                  <strong className="text-4xl font-black text-slate-900">+{Math.floor(clickCount * 2.2)}</strong>
                  <span className="text-xs text-slate-600 font-medium">novas chamadas & rotas iniciadas</span>
               </div>
               {/* Flat Clean Bars */}
               <div className="flex items-end gap-1.5 h-10 pt-2">
                  {[25, 40, 35, 60, 75, 90, 100].map((height, idx) => (
                     <div key={idx} className="flex-1 bg-blue-600 rounded-t" style={{ height: `${height}%` }} />
                  ))}
               </div>
            </div>
         </div>

         {/* RIGHT: REALISTIC GOOGLE SERP & GMN MOCKUP */}
         <div className="lg:col-span-7 flex flex-col md:flex-row gap-6 items-stretch justify-center">
            
            {/* MOCKUP 1: REALISTIC GOOGLE MOBILE SEARCH (SERP) */}
            <div className="flex-1 max-w-[320px] bg-white rounded-3xl border border-slate-300 overflow-hidden text-left flex flex-col mx-auto md:mx-0">
               {/* Phone Top Notch */}
               <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                     {/* Authentic Google 4-Color Logo */}
                     <span className="font-black text-sm tracking-tighter">
                        <span className="text-[#4285F4]">G</span>
                        <span className="text-[#EA4335]">o</span>
                        <span className="text-[#FBBC05]">o</span>
                        <span className="text-[#4285F4]">g</span>
                        <span className="text-[#34A853]">l</span>
                        <span className="text-[#EA4335]">e</span>
                     </span>
                  </div>
                  <div className="w-16 h-3 bg-slate-300 rounded-full" />
               </div>

               {/* Search Bar */}
               <div className="p-3 bg-white border-b border-slate-100">
                  <div className="w-full bg-slate-100 border border-slate-200 rounded-full py-2 px-3 flex items-center gap-2 text-xs">
                     <Search className="w-3.5 h-3.5 text-slate-400" />
                     <span className="font-medium text-slate-800 truncate">melhor empresa em minha cidade</span>
                  </div>
               </div>

               {/* SERP Search Results */}
               <div className="p-3 space-y-3 bg-[#F8FAFC] flex-1 text-xs">
                  {/* SPONSORED RESULT #1 */}
                  <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1.5">
                     <div className="flex items-center gap-1.5 text-[10px]">
                        <div className="w-4 h-4 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center text-[9px]">Y</div>
                        <span className="font-bold text-slate-900">Sua Empresa Oficial</span>
                        <span className="text-slate-400">• https://www.suaempresa.com.br</span>
                     </div>
                     <h3 className="font-bold text-blue-800 text-sm hover:underline cursor-pointer">
                        Sua Empresa - Líder de Mercado na Região
                     </h3>
                     <p className="text-[11px] text-slate-600 leading-snug">
                        Atendimento especializado, orçamento rápido e mais de 10.000 clientes atendidos. Confira nossos serviços exclusivos.
                     </p>
                     <div className="flex gap-2 pt-1">
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold border border-blue-100">
                           💬 WhatsApp Direct
                        </span>
                        <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[10px] font-bold border border-slate-200">
                           ⭐ Avaliações
                        </span>
                     </div>
                  </div>

                  {/* ORGANIC RESULT #2 */}
                  <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-1">
                     <div className="flex items-center gap-1.5 text-[10px]">
                        <div className="w-4 h-4 rounded-full bg-slate-200 text-slate-600 font-bold flex items-center justify-center text-[9px]">Y</div>
                        <span className="font-bold text-slate-800">Catálogo Online</span>
                        <span className="text-slate-400">• /produtos</span>
                     </div>
                     <h4 className="font-bold text-blue-800 text-xs hover:underline cursor-pointer">
                        Conheça Nosso Portfólio de Serviços Completo
                     </h4>
                  </div>
               </div>
            </div>

            {/* MOCKUP 2: REALISTIC GOOGLE MY BUSINESS (GMN) PANEL */}
            <div className="flex-1 max-w-[340px] bg-white rounded-3xl border border-slate-300 overflow-hidden text-left flex flex-col mx-auto md:mx-0">
               
               {/* Cover Image & Map Header */}
               <div className="h-32 bg-slate-200 relative overflow-hidden">
                  <img 
                     src="https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=400&auto=format&fit=crop" 
                     className="w-full h-full object-cover" 
                     alt="Google Maps Location" 
                  />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-bold text-slate-900 border border-slate-200 flex items-center gap-1">
                     <MapPin className="w-3 h-3 text-red-600" />
                     <span>Google Maps</span>
                  </div>
               </div>

               {/* GMN Business Info Card */}
               <div className="p-4 space-y-3 bg-white flex-1">
                  <div>
                     <div className="flex items-center gap-1.5">
                        <h3 className="font-black text-slate-900 text-base">Sua Empresa Ltda</h3>
                        <CheckCircle className="w-4 h-4 text-blue-600 fill-blue-50" />
                     </div>
                     <div className="flex items-center gap-1 text-xs mt-1 text-slate-600">
                        <span className="font-black text-amber-500">4.9</span>
                        <div className="flex text-amber-400">
                           {[1, 2, 3, 4, 5].map(star => (
                              <Star key={star} className="w-3 h-3 fill-amber-400 stroke-amber-400" />
                           ))}
                        </div>
                        <span className="text-slate-500 font-medium">(842 avaliações)</span>
                     </div>
                  </div>

                  <div className="text-xs text-slate-600 space-y-1">
                     <div className="flex items-center gap-1.5 text-emerald-600 font-bold">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        Aberto agora • Fecha às 18:00
                     </div>
                     <p className="text-slate-500">Av. Principal, 1000 - Centro, Sua Cidade/UF</p>
                  </div>

                  {/* Action Buttons (Realistic GMN UI) */}
                  <div className="grid grid-cols-3 gap-2 pt-2">
                     <button className="flex flex-col items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-xl border border-blue-200 transition-colors">
                        <Phone className="w-4 h-4 mb-1" />
                        <span className="text-[10px] font-bold">Ligar</span>
                     </button>
                     <button className="flex flex-col items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-xl border border-blue-200 transition-colors">
                        <MapPin className="w-4 h-4 mb-1" />
                        <span className="text-[10px] font-bold">Rotas</span>
                     </button>
                     <button className="flex flex-col items-center justify-center bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-xl border border-blue-200 transition-colors">
                        <Globe className="w-4 h-4 mb-1" />
                        <span className="text-[10px] font-bold">Website</span>
                     </button>
                  </div>
               </div>

            </div>

         </div>

      </div>
    </div>
  );
};
